import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';
import '../../styles/digitalReceipt.css';

const BAR_COUNT = 46;

// Deterministic per-order "barcode" - decorative only, never meant to scan as a real barcode.
// Seeded by the order id so it looks stable if the receipt re-renders, rather than jittering.
const seededBars = (seed) => {
  let x = 0;
  for (let i = 0; i < String(seed).length; i++) x = (x * 31 + String(seed).charCodeAt(i)) | 0;
  const rand = () => {
    x = (x * 1103515245 + 12345) | 0;
    return ((x >>> 0) % 1000) / 1000;
  };
  return Array.from({ length: BAR_COUNT }, () => ({
    width: 1 + Math.round(rand() * 2),
    height: 55 + Math.round(rand() * 45),
  }));
};

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

// Keyed on PaymentView's PAYMENT_MODES numeric codes (1=cash/cod, 2=eftpos, 3=online,
// 4=outofsystem) - `paymentMethod` here is always one of those numbers, never a string.
const PAYMENT_METHOD_LABELS = {
  1: 'Cash',
  2: 'EFTPOS',
  3: 'Online',
  4: 'Other',
};

/**
 * The "Digital Receipt Animation" POS setting (see SettingsPage / ConfigsController::POS_SETTINGS)
 * shows this after a payment completes - an on-screen docket that visually prints out from a slot
 * at the top, for the customer to see rather than (or alongside) a physical printed copy. Fetches
 * its own line items by order id so it stays a self-contained "given an order, show its receipt"
 * component instead of threading item state through PaymentView/CheckoutItem/CheckoutSummary.
 */
const DigitalReceiptModal = ({ isOpen, orderId, tableId, paymentMethod, summary, tableLabel = 'Table', serverName, onClose }) => {
  const [items, setItems] = useState([]);
  const [orderName, setOrderName] = useState('');
  const [loading, setLoading] = useState(false);
  // isOpen isn't read inside, but its change is exactly what should recompute "now" - a fresh
  // open should stamp the current time, not whatever moment the component first mounted.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const printedAt = useMemo(() => new Date(), [isOpen]);
  const bars = useMemo(() => seededBars(orderId || tableId || 'receipt'), [orderId, tableId]);
  // Same localStorage key Header.js already reads for the nav bar's restaurant name/logo -
  // reused here rather than re-fetching it, so the docket's header names the actual restaurant
  // instead of a generic app label.
  const restaurantName = localStorage.getItem('restaurant_name') || 'OrderArt POS';

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const fetchItems = async () => {
      setLoading(true);
      try {
        // Prefer order_id over table_id: by the time this shows, payment has already completed
        // and the table itself may already be free for a new order, so a table-based lookup can
        // come back empty - unlike order_id, which resolves this exact order regardless of
        // whether its table has since been freed up.
        let url = apiUrls.CHECKOUT_DISH_DETAILS;
        if (orderId) url += `?order_id=${orderId}`;
        else if (tableId) url += `?table_id=${tableId}`;

        const res = await callAPI(url, {}, 'GET');
        const data = res?.data?.data || {};
        if (cancelled) return;

        const lines = [
          ...(data.items || [])
            .filter((i) => !i.item_deleted)
            .map((i) => ({
              key: `item-${i.item_id}`,
              label: i.product_size ? `${i.product_name} - ${i.product_size}` : i.product_name,
              price: i.total_price,
            })),
          ...(data.data_deals || []).map((d, idx) => ({
            key: `deal-${idx}`,
            label: d.deal_name || 'Deal',
            price: d.totalwithdeals,
          })),
          ...(data.data_half_and_half || []).map((h, idx) => ({
            key: `hnh-${idx}`,
            label: h.product_name || 'Half & Half',
            price: h.total_price,
          })),
        ];

        setItems(lines);
        setOrderName(data.order_name || '');
      } catch (err) {
        console.error('Failed to load receipt items', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchItems();
    return () => { cancelled = true; };
  }, [isOpen, orderId, tableId]);

  const paymentLabel = PAYMENT_METHOD_LABELS[paymentMethod] || paymentMethod || '-';

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="digitalReceiptBackdrop">
          <div className="digitalReceiptSlot" />
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            style={{ overflow: 'hidden', width: 320, flexShrink: 0 }}
          >
            <div className="digitalReceiptPaper">
              <div className="digitalReceiptHeader">
                <div className="digitalReceiptBrand">{restaurantName}</div>
                <div className="digitalReceiptDate">
                  {printedAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  {' '}
                  {printedAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>

              <hr className="digitalReceiptDivider" />

              {loading ? (
                <div className="digitalReceiptLoading">Loading receipt...</div>
              ) : items.length === 0 ? (
                <div className="digitalReceiptEmpty">No items on this order</div>
              ) : (
                items.map((line) => (
                  <div className="digitalReceiptRow" key={line.key}>
                    <span className="rLabel">{line.label}</span>
                    <span className="rValue">{money(line.price)}</span>
                  </div>
                ))
              )}

              <hr className="digitalReceiptDivider" />

              <div className="digitalReceiptRow">
                <span className="rLabel">Subtotal</span>
                <span className="rValue">{money(summary?.gross_total)}</span>
              </div>
              {summary?.loyalty_discount > 0 && (
                <div className="digitalReceiptRow digitalReceiptRow--discount">
                  <span className="rLabel">Loyalty Discount</span>
                  <span className="rValue">- {money(summary.loyalty_discount)}</span>
                </div>
              )}
              {summary?.global_surcharges?.map((s, i) => (
                <div className="digitalReceiptRow" key={`surcharge-${i}`}>
                  <span className="rLabel">{s.charge_name}</span>
                  <span className="rValue">{money(s.total_expenses)}</span>
                </div>
              ))}
              {summary?.delivery_fee > 0 && (
                <div className="digitalReceiptRow">
                  <span className="rLabel">Delivery Fee</span>
                  <span className="rValue">{money(summary.delivery_fee)}</span>
                </div>
              )}
              {summary?.offer_discount > 0 && (
                <div className="digitalReceiptRow digitalReceiptRow--discount">
                  <span className="rLabel">Promo{summary.offer_name ? ` (${summary.offer_name})` : ''}</span>
                  <span className="rValue">- {money(summary.offer_discount)}</span>
                </div>
              )}
              <div className="digitalReceiptRow digitalReceiptRow--total">
                <span className="rLabel">Total</span>
                <span className="rValue">{money(summary?.net_total)}</span>
              </div>

              <hr className="digitalReceiptDivider" />

              <div className="digitalReceiptMeta">
                <div className="digitalReceiptRow digitalReceiptRow--muted">
                  <span className="rLabel">Payment</span>
                  <span className="rValue">{paymentLabel}</span>
                </div>
                {summary?.table?.name && (
                  <div className="digitalReceiptRow digitalReceiptRow--muted">
                    <span className="rLabel">{tableLabel}</span>
                    <span className="rValue">{summary.table.name}</span>
                  </div>
                )}
                {orderName && (
                  <div className="digitalReceiptRow digitalReceiptRow--muted">
                    <span className="rLabel">Order</span>
                    <span className="rValue">{orderName}</span>
                  </div>
                )}
                {serverName && (
                  <div className="digitalReceiptRow digitalReceiptRow--muted">
                    <span className="rLabel">Server</span>
                    <span className="rValue">{serverName}</span>
                  </div>
                )}
              </div>

              <div className="digitalReceiptPoweredBy">Powered by OrderArt</div>

              <div className="digitalReceiptBarcode">
                {bars.map((bar, i) => (
                  <span key={i} style={{ width: bar.width, height: `${bar.height}%` }} />
                ))}
              </div>
            </div>
          </motion.div>

          <button type="button" className="digitalReceiptDoneBtn" onClick={onClose}>
            Done
          </button>
        </div>
      )}
    </AnimatePresence>
  );
};

export default DigitalReceiptModal;
